import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, ChevronDown, ChevronRight, Pencil, Trash2, Search, ChevronsDown, ChevronsUp, MoreHorizontal, ArrowDownAZ, GripVertical, Copy } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function SortableProductRow({ prod, indent, salesStats, sym, toggleProductActive, openEditProduct, deleteProduct, onCopy }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: prod.product_id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 10 : "auto" };
  return (
    <div ref={setNodeRef} style={style} {...attributes} className={`grid grid-cols-[minmax(0,1fr)_80px_80px_60px_60px_60px_100px] gap-2 items-center px-4 py-2 hover:bg-slate-50 group ${isDragging ? "bg-blue-50 shadow-md ring-1 ring-blue-300" : ""}`} data-testid={`prod-row-${prod.product_id}`}>
      <div className={`flex items-center gap-1.5 ${indent} min-w-0`}>
        <button {...listeners} className="cursor-grab active:cursor-grabbing p-0.5 text-slate-300 hover:text-slate-500 touch-none flex-shrink-0" data-testid={`drag-prod-${prod.product_id}`}>
          <GripVertical className="h-3.5 w-3.5" />
        </button>
        <span className="text-sm text-slate-700 truncate">{prod.name}</span>
      </div>
      <div className="text-right text-sm text-slate-500">{prod.cost_price > 0 ? prod.cost_price.toFixed(2) : ""}</div>
      <div className="text-right text-sm font-medium text-slate-800">{prod.price > 0 ? prod.price.toFixed(2) : ""}</div>
      <div className="text-center">
        {salesStats[prod.product_id] ? <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-200">{salesStats[prod.product_id]}</Badge> : <span className="text-xs text-slate-300">0</span>}
      </div>
      <div className="text-center text-sm text-slate-500">{prod.sort_order}</div>
      <div className="text-center"><Checkbox checked={prod.is_active} onCheckedChange={() => toggleProductActive(prod)} className="border-slate-300" /></div>
      <div className="text-right flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={() => onCopy(prod)} className="p-1 rounded hover:bg-emerald-100" title="Copy product" data-testid={`copy-prod-${prod.product_id}`}><Copy className="h-3.5 w-3.5 text-emerald-600" /></button>
        <button onClick={() => openEditProduct(prod)} className="p-1 rounded hover:bg-blue-100" data-testid={`edit-prod-${prod.product_id}`}><Pencil className="h-3.5 w-3.5 text-[var(--brand-primary)]" /></button>
        <button onClick={() => deleteProduct(prod.product_id)} className="p-1 rounded hover:bg-red-100" data-testid={`del-prod-${prod.product_id}`}><Trash2 className="h-3.5 w-3.5 text-red-500" /></button>
      </div>
    </div>
  );
}

export default function ProductsPage() {
  const navigate = useNavigate();
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCats, setExpandedCats] = useState({});
  const [expandedSubcats, setExpandedSubcats] = useState({});
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [salesStats, setSalesStats] = useState({});
  const [salesPeriod, setSalesPeriod] = useState("ltd");

  // Copy product dialog
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [copySource, setCopySource] = useState(null);
  const [copyName, setCopyName] = useState("");
  const [copyCategoryId, setCopyCategoryId] = useState("");

  // Dialog states
  const [dialogType, setDialogType] = useState(null); // 'category' | 'subcategory' | 'product'
  const [editing, setEditing] = useState(null);
  const [parentContext, setParentContext] = useState({}); // { category_id, subcategory_id }
  const [catForm, setCatForm] = useState({ name: "", color: "#2563EB", sort_order: 0 });
  const [subcatForm, setSubcatForm] = useState({ name: "", category_id: "", sort_order: 0 });
  const [prodForm, setProdForm] = useState({ name: "", price: "", cost_price: "", category_id: "", subcategory_id: "", sort_order: 0, is_active: true });

  const fetchTree = useCallback(async () => {
    try {
      const [treeRes, statsRes] = await Promise.all([
        api.get(`/products/tree?active_only=${showActiveOnly}`),
        api.get(`/products/sales-stats?period=${salesPeriod}`),
      ]);
      setTree(treeRes.data);
      setSalesStats(statsRes.data);
    } catch (err) {
      toast.error("Failed to load product tree");
    } finally {
      setLoading(false);
    }
  }, [showActiveOnly, salesPeriod]);

  useEffect(() => { fetchTree(); }, [fetchTree]);

  const toggleCat = (catId) => setExpandedCats((p) => ({ ...p, [catId]: !p[catId] }));
  const toggleSubcat = (scId) => setExpandedSubcats((p) => ({ ...p, [scId]: !p[scId] }));
  const expandAll = () => {
    const cats = {}; const scs = {};
    tree.forEach((c) => { cats[c.category_id] = true; c.subcategories?.forEach((s) => { scs[s.subcategory_id] = true; }); });
    setExpandedCats(cats); setExpandedSubcats(scs);
  };
  const collapseAll = () => { setExpandedCats({}); setExpandedSubcats({}); };

  // === Category CRUD ===
  const openAddCategory = () => {
    setEditing(null);
    setCatForm({ name: "", color: "#2563EB", sort_order: tree.length + 1 });
    setDialogType("category");
  };
  const openEditCategory = (cat) => {
    setEditing(cat);
    setCatForm({ name: cat.name, color: cat.color || "#2563EB", sort_order: cat.sort_order || 0 });
    setDialogType("category");
  };
  const saveCategory = async () => {
    try {
      const payload = { ...catForm, sort_order: parseInt(catForm.sort_order) || 0, icon: "" };
      if (editing) { await api.put(`/categories/${editing.category_id}`, payload); toast.success("Category updated"); }
      else { await api.post("/categories", payload); toast.success("Category created"); }
      setDialogType(null); fetchTree();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const deleteCategory = async (catId) => {
    if (!window.confirm("Delete category and all its subcategories/products?")) return;
    try { await api.delete(`/categories/${catId}`); toast.success("Deleted"); fetchTree(); }
    catch { toast.error("Failed to delete"); }
  };

  // === Subcategory CRUD ===
  const openAddSubcategory = (categoryId) => {
    setEditing(null);
    setParentContext({ category_id: categoryId });
    const cat = tree.find((c) => c.category_id === categoryId);
    setSubcatForm({ name: "", category_id: categoryId, sort_order: (cat?.subcategories?.length || 0) + 1 });
    setDialogType("subcategory");
  };
  const openEditSubcategory = (sc) => {
    setEditing(sc);
    setSubcatForm({ name: sc.name, category_id: sc.category_id, sort_order: sc.sort_order || 0 });
    setDialogType("subcategory");
  };
  const saveSubcategory = async () => {
    try {
      const payload = { ...subcatForm, sort_order: parseInt(subcatForm.sort_order) || 0, is_active: true };
      if (editing) { await api.put(`/subcategories/${editing.subcategory_id}`, payload); toast.success("Subcategory updated"); }
      else { await api.post("/subcategories", payload); toast.success("Subcategory created"); }
      setDialogType(null); fetchTree();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const deleteSubcategory = async (scId) => {
    if (!window.confirm("Delete this subcategory?")) return;
    try { await api.delete(`/subcategories/${scId}`); toast.success("Deleted"); fetchTree(); }
    catch { toast.error("Failed to delete"); }
  };

  // === Product CRUD ===
  const openAddProduct = (categoryId, subcategoryId = "") => {
    navigate(`/dashboard/products/new?cat=${categoryId}&subcat=${subcategoryId}`);
  };
  const openEditProduct = (prod) => {
    navigate(`/dashboard/products/${prod.product_id}`);
  };
  const saveProduct = async () => {
    try {
      const payload = {
        ...prodForm, price: parseFloat(prodForm.price) || 0, cost_price: parseFloat(prodForm.cost_price) || 0,
        sort_order: parseInt(prodForm.sort_order) || 0, description: "", sku: "", barcode: "", image_url: "", stock_quantity: 0,
      };
      if (editing) { await api.put(`/products/${editing.product_id}`, payload); toast.success("Product updated"); }
      else { await api.post("/products", payload); toast.success("Product created"); }
      setDialogType(null); fetchTree();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const deleteProduct = async (prodId) => {
    if (!window.confirm("Delete this product?")) return;
    try { await api.delete(`/products/${prodId}`); toast.success("Deleted"); fetchTree(); }
    catch { toast.error("Failed to delete"); }
  };
  const toggleProductActive = async (prod) => {
    try {
      await api.put(`/products/${prod.product_id}`, { is_active: !prod.is_active });
      fetchTree();
    } catch { toast.error("Failed to update"); }
  };

  const openCopyProduct = (prod) => {
    setCopySource(prod);
    setCopyName(`${prod.name} (Copy)`);
    setCopyCategoryId(prod.category_id || "");
    setShowCopyDialog(true);
  };

  const saveCopyProduct = async () => {
    if (!copyName.trim()) { toast.error("Name is required"); return; }
    if (!copyCategoryId) { toast.error("Select a category"); return; }
    try {
      // Fetch full product data
      const res = await api.get(`/products/${copySource.product_id}`);
      const src = res.data;
      const payload = {
        name: copyName.trim(),
        description: src.description || "",
        price: src.price || 0,
        cost_price: src.cost_price || 0,
        category_id: copyCategoryId,
        subcategory_id: copyCategoryId === src.category_id ? (src.subcategory_id || "") : "",
        is_active: true,
        sort_order: 0,
        size_group_id: src.size_group_id || "",
        size_prices: src.size_prices || [],
        modifier_group_ids: src.modifier_group_ids || [],
        default_modifier_ids: src.default_modifier_ids || [],
        tax_rate_ids: src.tax_rate_ids || [],
        tax_ids: src.tax_ids || [],
        dining_type_ids: src.dining_type_ids || [],
        modifier_size_availability: src.modifier_size_availability || {},
        is_combo: src.is_combo || false,
        combo_slots: src.combo_slots || [],
        recipe_ingredients: src.recipe_ingredients || [],
        allow_negative_stock: src.allow_negative_stock || false,
        image_url: src.image_url || "",
      };
      await api.post("/products", payload);
      toast.success(`Copied as "${copyName.trim()}"`);
      setShowCopyDialog(false);
      fetchTree();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to copy"); }
  };

  // Filter by search
  const matchesSearch = (name) => !search || name.toLowerCase().includes(search.toLowerCase());

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleProductDragEnd = async (event, productList, contextType, contextId) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = productList.findIndex((p) => p.product_id === active.id);
    const newIdx = productList.findIndex((p) => p.product_id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const reordered = arrayMove(productList, oldIdx, newIdx);
    const items = reordered.map((p, i) => ({ product_id: p.product_id, sort_order: i + 1 }));
    try {
      await api.put("/products/batch-reorder", { items });
      fetchTree();
    } catch { toast.error("Failed to reorder"); }
  };

  const sortProductsAZ = async (productList) => {
    const sorted = [...productList].sort((a, b) => a.name.localeCompare(b.name));
    const items = sorted.map((p, i) => ({ product_id: p.product_id, sort_order: i + 1 }));
    try {
      await api.put("/products/batch-reorder", { items });
      toast.success("Sorted A-Z");
      fetchTree();
    } catch { toast.error("Failed to sort"); }
  };

  if (loading) return <div className="p-8 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[var(--brand-primary)] border-t-transparent" /></div>;

  return (
    <div className="p-6" data-testid="products-page">
      {/* Header */}
      <div className="bg-[var(--brand-primary)] text-white px-4 py-3 rounded-t-lg flex items-center justify-between" data-testid="products-header">
        <h1 className="font-heading text-lg font-bold">Products</h1>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="text-white hover:bg-white/20" onClick={() => setSearch(search ? "" : " ")} data-testid="products-search-toggle">
            <Search className="h-4 w-4" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/20" data-testid="products-menu-btn">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={openAddCategory}>Add Category</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white border-x border-b border-slate-200 px-4 py-2 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3 text-sm">
          <button onClick={expandAll} className="text-[var(--brand-primary)] hover:underline font-medium" data-testid="expand-all-btn">expand all</button>
          <span className="text-slate-300">|</span>
          <button onClick={collapseAll} className="text-[var(--brand-primary)] hover:underline font-medium" data-testid="collapse-all-btn">collapse all</button>
          <span className="text-slate-300 ml-2">|</span>
          <span className="text-xs text-slate-400 ml-1">Sales:</span>
          {["day", "week", "month", "ltd"].map((p) => (
            <button key={p} onClick={() => setSalesPeriod(p)} className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${salesPeriod === p ? "bg-[var(--brand-primary)] text-white" : "text-slate-500 hover:bg-slate-100"}`} data-testid={`sales-period-${p}`}>
              {p === "ltd" ? "LTD" : p.charAt(0).toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          {search !== "" && (
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <Input placeholder="Search..." value={search === " " ? "" : search} onChange={(e) => setSearch(e.target.value)} className="h-8 pl-7 text-sm w-48" data-testid="products-search-input" autoFocus />
            </div>
          )}
          <Select value={showActiveOnly ? "active" : "all"} onValueChange={(v) => setShowActiveOnly(v === "active")}>
            <SelectTrigger className="h-8 w-32 text-xs" data-testid="products-active-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Show All</SelectItem>
              <SelectItem value="active">Show Active</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={openAddCategory} data-testid="add-category-top-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add
          </Button>
        </div>
      </div>

      {/* Column Headers */}
      <div className="bg-slate-50 border-x border-b border-slate-200 px-4 py-2 grid grid-cols-[minmax(0,1fr)_80px_80px_60px_60px_60px_100px] gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wider" data-testid="products-col-headers">
        <div>Category &gt; Subcategory &gt; Product</div>
        <div className="text-right">Cost</div>
        <div className="text-right">Price</div>
        <div className="text-center">Sold</div>
        <div className="text-center">Sort</div>
        <div className="text-center">Active</div>
        <div className="text-right">Actions</div>
      </div>

      {/* Tree */}
      <div className="bg-white border-x border-b border-slate-200 rounded-b-lg divide-y divide-slate-100" data-testid="products-tree">
        {tree.map((cat) => {
          const catExpanded = expandedCats[cat.category_id];
          const hasChildren = (cat.subcategories?.length > 0) || (cat.direct_products?.length > 0);
          if (search && !matchesSearch(cat.name) && !cat.subcategories?.some((s) => matchesSearch(s.name) || s.products?.some((p) => matchesSearch(p.name))) && !cat.direct_products?.some((p) => matchesSearch(p.name))) return null;

          return (
            <div key={cat.category_id} data-testid={`cat-row-${cat.category_id}`}>
              {/* Category Row */}
              <div className="grid grid-cols-[minmax(0,1fr)_80px_80px_60px_60px_60px_100px] gap-2 items-center px-4 py-2.5 bg-blue-50/60 hover:bg-blue-50 cursor-pointer group" onClick={() => toggleCat(cat.category_id)}>
                <div className="flex items-center gap-2 min-w-0">
                  <Checkbox className="border-slate-300" data-testid={`cat-check-${cat.category_id}`} onClick={(e) => e.stopPropagation()} />
                  {hasChildren ? (
                    catExpanded ? <ChevronDown className="h-4 w-4 text-[var(--brand-primary)] flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-[var(--brand-primary)] flex-shrink-0" />
                  ) : <div className="w-4" />}
                  <span className="font-semibold text-sm text-slate-800 truncate">{cat.name}</span>
                </div>
                <div />
                <div />
                <div />
                <div className="text-center text-sm text-slate-500">{cat.sort_order}</div>
                <div className="text-center"><Checkbox checked={cat.is_active} disabled className="border-slate-300" /></div>
                <div className="text-right flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {cat.direct_products?.length > 1 && <button onClick={(e) => { e.stopPropagation(); sortProductsAZ(cat.direct_products); }} className="p-1 rounded hover:bg-emerald-100" title="Sort products A-Z" data-testid={`sort-az-cat-${cat.category_id}`}><ArrowDownAZ className="h-3.5 w-3.5 text-emerald-600" /></button>}
                  <button onClick={(e) => { e.stopPropagation(); openEditCategory(cat); }} className="p-1 rounded hover:bg-blue-100" data-testid={`edit-cat-${cat.category_id}`}><Pencil className="h-3.5 w-3.5 text-[var(--brand-primary)]" /></button>
                  <button onClick={(e) => { e.stopPropagation(); deleteCategory(cat.category_id); }} className="p-1 rounded hover:bg-red-100" data-testid={`del-cat-${cat.category_id}`}><Trash2 className="h-3.5 w-3.5 text-red-500" /></button>
                </div>
              </div>

              {/* Expanded Content */}
              {catExpanded && (
                <>
                  {/* Subcategories */}
                  {cat.subcategories?.map((sc) => {
                    const scExpanded = expandedSubcats[sc.subcategory_id];
                    if (search && !matchesSearch(sc.name) && !sc.products?.some((p) => matchesSearch(p.name))) return null;
                    return (
                      <div key={sc.subcategory_id} data-testid={`subcat-row-${sc.subcategory_id}`}>
                        <div className="grid grid-cols-[minmax(0,1fr)_80px_80px_60px_60px_60px_100px] gap-2 items-center px-4 py-2 hover:bg-slate-50 cursor-pointer group" onClick={() => toggleSubcat(sc.subcategory_id)}>
                          <div className="flex items-center gap-2 pl-8 min-w-0">
                            <Checkbox className="border-slate-300" onClick={(e) => e.stopPropagation()} />
                            {sc.products?.length > 0 ? (
                              scExpanded ? <ChevronDown className="h-4 w-4 text-[var(--brand-primary)] flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-[var(--brand-primary)] flex-shrink-0" />
                            ) : <Plus className="h-4 w-4 text-slate-300 flex-shrink-0" />}
                            <span className="font-medium text-sm text-slate-700 truncate">{sc.name}</span>
                          </div>
                          <div />
                          <div />
                          <div />
                          <div className="text-center text-sm text-slate-500">{sc.sort_order}</div>
                          <div className="text-center"><Checkbox checked={sc.is_active} disabled className="border-slate-300" /></div>
                          <div className="text-right flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {sc.products?.length > 1 && <button onClick={(e) => { e.stopPropagation(); sortProductsAZ(sc.products); }} className="p-1 rounded hover:bg-emerald-100" title="Sort A-Z" data-testid={`sort-az-${sc.subcategory_id}`}><ArrowDownAZ className="h-3.5 w-3.5 text-emerald-600" /></button>}
                            <button onClick={(e) => { e.stopPropagation(); openEditSubcategory(sc); }} className="p-1 rounded hover:bg-blue-100" data-testid={`edit-subcat-${sc.subcategory_id}`}><Pencil className="h-3.5 w-3.5 text-[var(--brand-primary)]" /></button>
                            <button onClick={(e) => { e.stopPropagation(); deleteSubcategory(sc.subcategory_id); }} className="p-1 rounded hover:bg-red-100" data-testid={`del-subcat-${sc.subcategory_id}`}><Trash2 className="h-3.5 w-3.5 text-red-500" /></button>
                          </div>
                        </div>

                        {/* Products under subcategory */}
                        {scExpanded && sc.products?.length > 0 && (
                          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleProductDragEnd(e, sc.products)}>
                            <SortableContext items={sc.products.map((p) => p.product_id)} strategy={verticalListSortingStrategy}>
                              {sc.products.map((prod) => {
                                if (search && !matchesSearch(prod.name)) return null;
                                return <SortableProductRow key={prod.product_id} prod={prod} indent="pl-16" salesStats={salesStats} sym="$" toggleProductActive={toggleProductActive} openEditProduct={openEditProduct} deleteProduct={deleteProduct} onCopy={openCopyProduct} />;
                              })}
                            </SortableContext>
                          </DndContext>
                        )}

                        {/* + Product link under subcategory */}
                        {scExpanded && (
                          <div className="px-4 py-1.5 pl-24">
                            <button onClick={() => openAddProduct(cat.category_id, sc.subcategory_id)} className="text-xs text-[var(--brand-primary)] hover:underline font-medium" data-testid={`add-prod-in-${sc.subcategory_id}`}>+ Product</button>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Direct products (no subcategory) */}
                  {cat.direct_products?.length > 0 && (
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleProductDragEnd(e, cat.direct_products)}>
                      <SortableContext items={cat.direct_products.map((p) => p.product_id)} strategy={verticalListSortingStrategy}>
                        {cat.direct_products.map((prod) => {
                          if (search && !matchesSearch(prod.name)) return null;
                          return <SortableProductRow key={prod.product_id} prod={prod} indent="pl-4" salesStats={salesStats} sym="$" toggleProductActive={toggleProductActive} openEditProduct={openEditProduct} deleteProduct={deleteProduct} onCopy={openCopyProduct} />;
                        })}
                      </SortableContext>
                    </DndContext>
                  )}

                  {/* + Subcategory link */}
                  <div className="px-4 py-1.5 pl-12">
                    <button onClick={() => openAddSubcategory(cat.category_id)} className="text-xs text-[var(--brand-primary)] hover:underline font-medium" data-testid={`add-subcat-in-${cat.category_id}`}>+ Subcategory</button>
                    {!cat.subcategories?.length && (
                      <button onClick={() => openAddProduct(cat.category_id)} className="text-xs text-[var(--brand-primary)] hover:underline font-medium ml-4" data-testid={`add-prod-direct-${cat.category_id}`}>+ Product</button>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* === DIALOGS === */}
      {/* Category Dialog */}
      <Dialog open={dialogType === "category"} onOpenChange={() => setDialogType(null)}>
        <DialogContent data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit Category" : "New Category"}</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Categories are the top-level groups shown on the POS</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div>
              <Label className="text-xs text-slate-500">Name</Label>
              <Input value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} data-testid="cat-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Color</Label>
                <div className="flex gap-2 mt-1">
                  <input type="color" value={catForm.color} onChange={(e) => setCatForm({ ...catForm, color: e.target.value })} className="h-9 w-12 rounded border cursor-pointer" />
                  <Input value={catForm.color} onChange={(e) => setCatForm({ ...catForm, color: e.target.value })} className="flex-1" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Sort Order</Label>
                <Input type="number" value={catForm.sort_order} onChange={(e) => setCatForm({ ...catForm, sort_order: e.target.value })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogType(null)}>Cancel</Button>
            <Button onClick={saveCategory} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="cat-save-btn">{editing ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subcategory Dialog */}
      <Dialog open={dialogType === "subcategory"} onOpenChange={() => setDialogType(null)}>
        <DialogContent data-testid="subcategory-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit Subcategory" : "New Subcategory"}</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Subcategories group products within a category</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div>
              <Label className="text-xs text-slate-500">Name</Label>
              <Input value={subcatForm.name} onChange={(e) => setSubcatForm({ ...subcatForm, name: e.target.value })} data-testid="subcat-name-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Sort Order</Label>
              <Input type="number" value={subcatForm.sort_order} onChange={(e) => setSubcatForm({ ...subcatForm, sort_order: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogType(null)}>Cancel</Button>
            <Button onClick={saveSubcategory} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="subcat-save-btn">{editing ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Dialog */}
      <Dialog open={dialogType === "product"} onOpenChange={() => setDialogType(null)}>
        <DialogContent data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit Product" : "New Product"}</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Products are the items customers can order</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div>
              <Label className="text-xs text-slate-500">Name</Label>
              <Input value={prodForm.name} onChange={(e) => setProdForm({ ...prodForm, name: e.target.value })} data-testid="prod-name-input" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Cost</Label>
                <Input type="number" step="0.01" value={prodForm.cost_price} onChange={(e) => setProdForm({ ...prodForm, cost_price: e.target.value })} data-testid="prod-cost-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Price</Label>
                <Input type="number" step="0.01" value={prodForm.price} onChange={(e) => setProdForm({ ...prodForm, price: e.target.value })} data-testid="prod-price-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Sort Order</Label>
                <Input type="number" value={prodForm.sort_order} onChange={(e) => setProdForm({ ...prodForm, sort_order: e.target.value })} />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={prodForm.is_active} onCheckedChange={(v) => setProdForm({ ...prodForm, is_active: v })} data-testid="prod-active-switch" />
              <Label className="text-sm">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogType(null)}>Cancel</Button>
            <Button onClick={saveProduct} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="prod-save-btn">{editing ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copy Product Dialog */}
      <Dialog open={showCopyDialog} onOpenChange={setShowCopyDialog}>
        <DialogContent className="sm:max-w-md" data-testid="copy-product-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Copy className="h-4 w-4 text-emerald-600" /> Copy Product</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Create a copy of <strong>{copySource?.name}</strong> with a new name and category.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div>
              <Label className="text-xs text-slate-500">New Name *</Label>
              <Input value={copyName} onChange={(e) => setCopyName(e.target.value)} placeholder="Product name" data-testid="copy-name-input" autoFocus />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Category *</Label>
              <Select value={copyCategoryId} onValueChange={setCopyCategoryId}>
                <SelectTrigger data-testid="copy-category-select"><SelectValue placeholder="Select category..." /></SelectTrigger>
                <SelectContent>
                  {tree.map((cat) => (
                    <SelectItem key={cat.category_id} value={cat.category_id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCopyDialog(false)}>Cancel</Button>
            <Button onClick={saveCopyProduct} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="copy-save-btn">
              <Copy className="h-4 w-4 mr-1" /> Copy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
